<template>
    <v-navigation-drawer
        ref="drawerRef"
        v-model="drawer"
        class="drawer__content"
        app
        dark
        right
        clipped
        width="260"
    >
        <v-toolbar flat class="toolbar">
            <v-toolbar-title>KDS</v-toolbar-title>

            <v-spacer />

            <v-menu bottom left transition="slide-y-transition">
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon>
                        <v-icon>far fa-ellipsis-v</v-icon>
                    </v-btn>
                </template>

                <v-list width="240">
                    <v-list-item key="heading">
                        <v-list-item-avatar>
                            <v-avatar class="primary" size="40">
                                <v-icon dark>fal fa-user</v-icon>
                            </v-avatar>
                        </v-list-item-avatar>

                        <v-list-item-content v-if="store.getters.user">
                            <v-list-item-title>{{ store.getters.user.name }}</v-list-item-title>
                        </v-list-item-content>

                        <v-list-item-content v-else-if="tenant">
                            <v-list-item-title>{{ tenant.name }}</v-list-item-title>
                            <v-list-item-subtitle>{{ $t('user_roles.default.super_admin') }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>

                    <v-divider />

                    <template v-for="(option, index) in menuOptions">
                        <v-list-item
                            v-if="option.available"
                            :key="index"
                            @click="option.onClick"
                        >
                            <v-list-item-action class="minw25 ml-1">
                                <v-icon>{{ option.icon }}</v-icon>
                            </v-list-item-action>
                            <v-list-item-title>{{ option.text }}</v-list-item-title>
                        </v-list-item>
                    </template>
                </v-list>
            </v-menu>
        </v-toolbar>

        <v-expansion-panels
            v-if="!showAllCounts"
            v-model="panel"
            multiple
        >
            <template v-for="(item, index) in items">
                <v-expansion-panel
                    v-if="item.enabled"
                    :key="index"
                    class="expansion-panel"
                >
                    <v-expansion-panel-header class="d-flex justify-start">
                        <div>
                            <v-icon size="16" class="mr-2">{{ item.icon }}</v-icon>

                            <span>{{ item.title }}</span>
                        </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                        <component
                            :is="item.component"
                            :all-items="allItemsAndCategories"
                            :selected-items="selectedItems"
                            :limited="limited"
                            @show-all-counts="onShowAllCounts"
                            @update-items="onItemsUpdate"
                        />
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </template>
        </v-expansion-panels>

        <v-sheet
            v-else
            class="pa-4"
            :height="totalListHeight"
        >
            <v-btn
                depressed
                text
                block
                style="padding-top: 5px"
                class="text-none d-flex justify-start align-start mb-3 ps-2"
                @click="showAllCounts = false"
            >
                <v-icon size="16" style="margin-right: 7px">far fa-arrow-left</v-icon>

                <span class="text-body-2">{{ $t('kds.drawer.all_day') }}</span>
            </v-btn>

            <all-day-items
                class="ml-2"
                :all-items="allItemsAndCategories"
                :selected-items="selectedItems"
                :height="totalCountListHeight"
                @update-items="onItemsUpdate"
            />
        </v-sheet>
    </v-navigation-drawer>
</template>

<script setup>
import { ref, computed, watch, defineExpose, onMounted, defineProps } from 'vue';
import { useAppStore } from '@/store/useAppStore';
import i18n from '@/i18n';
import useVuetify from '@/utilities/useVuetify';
import useRouter from '@/utilities/useRouter';
import TenantService from '@/services/TenantService';
import UserService from '@/services/UserService';

import useKitchenTickets from './useKitchenTickets';
import useKitchenTicketItems from './useKitchenTicketItems';

import AllDayItems from './AllDayItems.vue';
import Settings from './Settings.vue';
import Terminal from '@/models/Terminal';

const store = useAppStore();
const { $snackbar } = useVuetify();
const { router, route } = useRouter();

const { openTickets, selectedItems } = useKitchenTickets();
const { allItems, allItemsAndCategories } = useKitchenTicketItems(openTickets.value);

const props = defineProps({
    limited: {
        type: Boolean,
        default: true,
    }
})

const drawer = ref(false);
const drawerRef = ref(null);
const showAllCounts = ref(false);
const panel = ref([0, 1]);
const terminal = ref()

const isOnline = computed(() => store.state.isOnline);
const isDonePage = computed(() => route.value.name === 'kitchenDoneTickets');

const items = computed(() => [{
    enabled: !props.limited,
    title: i18n.t('kds.drawer.all_day'),
    icon: 'far fa-arrow-down',
    component: AllDayItems,
}, {
    enabled: true,
    title: i18n.t('kds.drawer.settings'),
    icon: 'far fa-cog',
    component: Settings,
}]);

const menuOptions = computed(() => [{
    available: !terminal.value?.isKitchenDisplaySystem() && !disableSales.value && UserService.allows('nav_access_tables') && store.getters.user,
    text: i18n.t('general.table_plan'),
    icon: 'fal fa-share-all',
    onClick: () => router.value.push({ name: 'tables' }).catch((err) => {})
}, {
    available: !terminal.value?.isKitchenDisplaySystem() && UserService.allows('nav_access_admin'),
    text: i18n.t('general.administration'),
    icon: 'fal fa-sliders-h',
    onClick: () => router.value.push({ name: 'adminDashboard' }).catch(err => {})
}, {
    available: !terminal.value?.isKitchenDisplaySystem() && !!store.getters.user,
    text: i18n.t('general.log_out'),
    icon: 'fal fa-sign-out',
    onClick: logout
}, {
    available: terminal.value?.isKitchenDisplaySystem() || !store.getters.user,
    text: i18n.t('general.manager_logout'),
    icon: 'fal fa-sign-out',
    onClick: logoutTenant
}])

const totalListHeight = computed(() => {
    const top = 0;
    const gutters = 64;
    const drawerHeight = drawerRef.value ? drawerRef.value.$el.clientHeight : 0;

    return drawerHeight - top - gutters;
});

const disableSales = computed(() => {
    const singleCashRegister = store.getters.tenant?.venue && !!store.getters.tenant.venue.settings.cash_register.single
    return !singleCashRegister && !store.getters.impersonate && store.getters.device?.platform === 'web' && process.env.VUE_APP_ENV === 'production'
})

const totalCountListHeight = computed(() => {
    return totalListHeight.value - 88;
});

onMounted(async () => {
    terminal.value = await Terminal.current()
})

watch(() => drawer.value, value => {
    if (value) {
        showAllCounts.value = false;
    }
});

watch(() => allItems.value, value => {
    const items = value.map((item) => item.id);
    selectedItems.value = selectedItems.value.filter((item) => items.includes(item.id));
});

// Close drawer when done page is active
// This is to prevent the drawer from being open when the done page is active using Electron
watch(() => isDonePage.value, value => {
    if (value) {
        drawer.value = false;
    }
});

const onShowAllCounts = () => {
    showAllCounts.value = true;
};

const onItemsUpdate = (item) => {
    if (selectedItems.value.map((selectedItem) => selectedItem.id).includes(item.id)) {
        selectedItems.value = selectedItems.value.filter((selectedItem) => selectedItem.id !== item.id);
    } else {
        selectedItems.value.push(item);
    }
};

const logoutTenant = () => {
    if (!isOnline.value) {
        $snackbar(i18n.t('connection.internet_offline_message_not_allowed'), 'error', { timeout: 10000 });
        return;
    }

    TenantService.logout();
    router.value.push({ name: 'tenantLogin' }).catch((err) => {});
};

const logout = () => {
    UserService.logout();
    router.value.push({ name: 'userLogin' }).catch((err) => {});
};

defineExpose({ drawer });
</script>

<style lang="scss" scoped>
.drawer__content {
    padding: 0 12px 12px 0;

    @media screen and (max-width: 1264px) {
        padding: 0 12px 12px 12px;
    }

    .toolbar {
        border-radius: 8px;
    }

    ::v-deep(.v-expansion-panel) {
        margin-top: 8px;
        border-radius: 8px !important;
        background-color: #1d1f29 !important;
    }

    ::v-deep(.v-sheet) {
        margin-top: 8px;
        border-radius: 8px;
        background-color: #1d1f29 !important;
    }
}

::v-deep(.v-navigation-drawer__content::-webkit-scrollbar) {
    width: 0px !important;
}

::v-deep(.v-navigation-drawer__border) {
    display: none;
}

.item__name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>
